import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createMemoryHistory, createRouter } from '@tanstack/react-router';
import { PostHogProvider } from 'posthog-js/react';
import { IntercomProvider } from 'react-use-intercom';
import { Splashscreen } from './components/splashscreen/splashscreen';
import { detectOS, isDesktop } from './lib/platform';
import { FreshsalesProvider } from './providers/integration-caller/freshsales-provider';
import { HubSpotProvider } from './providers/integration-caller/hubspot-provider';
import { SalesforceProvider } from './providers/integration-caller/salesforce-provider';
import { routeTree } from './routeTree.gen';
import { analytics } from './services/analytics';
import { hubspotService } from './services/hubspot';
import { nativeApi } from './services/native-api/native-api';
import { newRelicService } from './services/newrelic';
import { posthogClient } from './services/posthog';

// Track app start time for splash screen monitoring
if (typeof window !== 'undefined') {
  window.__appStartTime = Date.now();
}

const memoryHistory =
  nativeApi &&
  createMemoryHistory({
    // In dev: use pathname. In production: use hash or default to '/'
    initialEntries: [
      import.meta.env.DEV
        ? window.location.pathname || '/'
        : window.location.hash.replace('#', '') || '/',
    ],
  });

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
  },
});

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  history: memoryHistory,
  context: {
    queryClient,
  },
});

/**
 * @see Analytics-Amplitude
 */
analytics.initialize();

/**
 * @see Analytics-NewRelic
 */
newRelicService.initialize();
newRelicService.setCustomAttribute('platform', isDesktop() ? 'electron' : 'web', true);
newRelicService.setCustomAttribute('os', detectOS(), true);
newRelicService.setCustomAttribute('appVersion', APP_VERSION ?? 'unknown', true);

/**
 * @see Analytics-Sentry
 */
Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  debug: import.meta.env.DEV,
  environment: import.meta.env.DEV ? 'development' : 'production',
  release: APP_VERSION ?? 'development',
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
  tracesSampleRate: import.meta.env.DEV ? 1.0 : 0.2,
  maxBreadcrumbs: 50,
  beforeSend(event) {
    // Only send errors to Sentry, filter out info and warning level events
    if (event.level === 'info' || event.level === 'warning' || event.level === 'log') {
      return null;
    }

    // Drop New Relic browser agent internal error (DESKTOP-ALLO-N3)
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (
      frames?.some((f) => f.filename?.includes('@newrelic/browser-agent')) &&
      event.exception?.values?.[0]?.value?.includes('Cannot convert object to primitive value')
    ) {
      return null;
    }

    return event;
  },
});

/**
 * @see Hubspot
 */
hubspotService.initialize();

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export const App = () => {
  return (
    <Sentry.ErrorBoundary
      fallback={
        // Pass `error` as a boolean so Splashscreen's own Sentry.captureException
        // effect short-circuits — Sentry.ErrorBoundary already reports the
        // original Error via its own capture, no need to log it twice. The
        // client copy is static and doesn't read the Error instance anyway.
        <Splashscreen error kind="client" noTimeout />
      }
    >
      <PostHogProvider client={posthogClient}>
        <IntercomProvider
          appId={import.meta.env.VITE_INTERCOM_APP_ID}
          autoBoot={true}
          autoBootProps={{ hideDefaultLauncher: true, alignment: 'left' }}
        >
          <QueryClientProvider client={queryClient}>
            <HubSpotProvider>
              <FreshsalesProvider>
                <SalesforceProvider>
                  <RouterProvider router={router} />
                </SalesforceProvider>
              </FreshsalesProvider>
            </HubSpotProvider>
          </QueryClientProvider>
        </IntercomProvider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  );
};
